/**
 * Care Concierge cases API (Feature 106).
 *
 * The admin surface, and the only one that returns notes the team has not shared with the
 * advisor — the advisor-facing endpoints withhold them at the query.
 */

import { apiClient } from '@/core/api/client';
import { fileNameFromDisposition } from '@/shared/utils/downloadBlob';

const BASE_PATH = '/admin/care-concierge';

export type CaseStatus =
  | 'AWAITING_CLIENT'
  | 'SCHEDULED'
  | 'OCCURRED'
  | 'COMPLETED'
  | 'NOT_COMPLETED';

export type CaseRequestSource =
  | 'PAID_CONSULTATION'
  | 'SP_NOT_FOUND'
  | 'DIRECT_ADVISOR_REQUEST'
  | 'DISCOVERY_CALL';

/** What goes up: one of the two, never both — the column has a CHECK that says so. */
export type ReferredProviderEntry = {
  serviceProviderId?: string;
  freeTextName?: string;
};

/**
 * What comes back. The id rides along with the name so the editor can tell a provider that was
 * picked from the directory from one whose name was typed in — it saves the list wholesale, and
 * without the id every linked provider would degrade to a name on the next save.
 */
export type ReferredProvider = {
  serviceProviderId: string | null;
  name: string;
};

export type CaseFollowUp = {
  id: string;
  followUpOn: string;
  note: string | null;
};

export type ConciergeCase = {
  id: string;
  status: CaseStatus;
  requestSource: CaseRequestSource;
  payer: 'SPONSOR' | 'SELF_PAY' | null;
  personEmail: string | null;
  personName: string | null;
  /** Set when the person is a bQuest account (Feature 127). */
  clientUserId: string | null;
  advisorName: string | null;
  firmName: string | null;
  requestReceivedAt: string | null;
  invitedAt: string | null;
  scheduledAt: string | null;
  occurredAt: string | null;
  recommendationsProvidedAt: string | null;
  /** Admin surface only unless the share flag is set. */
  notes: string | null;
  sharedWithAdvisor: boolean;
  referredProviders: ReferredProvider[];
  followUps: CaseFollowUp[];
  hasCarePlan: boolean;
};

export type UpdateCasePayload = {
  status?: CaseStatus;
  requestSource?: CaseRequestSource;
  scheduledAt?: string;
  occurredAt?: string;
  requestReceivedAt?: string;
  recommendationsProvidedAt?: string;
  notes?: string;
  sharedWithAdvisor?: boolean;
  referredProviders?: ReferredProviderEntry[];
};

/**
 * Feature 127: the person is either an existing care seeker (clientUserId — name and email then
 * come from the account) or a typed name/email. advisorUserId attaches the advisor (and their
 * firm); inviteToApp sends that advisor's invitation to personEmail.
 */
export type CreateManualCasePayload = {
  requestSource: CaseRequestSource;
  clientUserId?: string;
  personName?: string;
  personEmail?: string;
  advisorUserId?: string;
  inviteToApp?: boolean;
  requestReceivedAt?: string;
  notes?: string;
};

/** The case, plus why an invitation that was asked for did not go out. */
export type ManualCaseCreated = ConciergeCase & { inviteWarning: string | null };

export async function fetchCases(status?: CaseStatus): Promise<ConciergeCase[]> {
  const response = await apiClient.get<ConciergeCase[]>(`${BASE_PATH}/cases`, {
    params: status ? { status } : undefined,
  });
  return response.data;
}

/**
 * Moving a case to NOT_COMPLETED returns its sponsored session, because the balance counts cases
 * that have not ended that way.
 */
export async function updateCase(
  caseId: string,
  payload: UpdateCasePayload,
): Promise<ConciergeCase> {
  const response = await apiClient.patch<ConciergeCase>(`${BASE_PATH}/cases/${caseId}`, payload);
  return response.data;
}

/** Record-keeping only: no emails go out and no sponsored session is touched. */
export async function createManualCase(
  payload: CreateManualCasePayload,
): Promise<ManualCaseCreated> {
  const response = await apiClient.post<ManualCaseCreated>(`${BASE_PATH}/cases`, payload);
  return response.data;
}

export async function addCaseFollowUp(
  caseId: string,
  followUpOn: string,
  note?: string,
): Promise<ConciergeCase> {
  const response = await apiClient.post<ConciergeCase>(
    `${BASE_PATH}/cases/${caseId}/follow-ups`,
    { followUpOn, ...(note ? { note } : {}) },
  );
  return response.data;
}

export async function removeCaseFollowUp(
  caseId: string,
  followUpId: string,
): Promise<ConciergeCase> {
  const response = await apiClient.delete<ConciergeCase>(
    `${BASE_PATH}/cases/${caseId}/follow-ups/${followUpId}`,
  );
  return response.data;
}

export type CarePlanSummary = {
  fileName: string | null;
  fileSize: number | null;
  uploadedAt: string | null;
};

/** The platform's PDF ceiling, checked here so an oversized file is refused before it uploads. */
export const CARE_PLAN_MAX_BYTES = 10 * 1024 * 1024;

export async function uploadCarePlan(
  caseId: string,
  file: File,
): Promise<CarePlanSummary> {
  const body = new FormData();
  body.append('file', file);

  // Overriding Content-Type per request is required, not optional: apiClient defaults every call
  // to application/json, and axios keeps that default over FormData — so the boundary never gets
  // written and multer sees no file at all. Same header every other upload in the app sets.
  const response = await apiClient.post<CarePlanSummary>(
    `${BASE_PATH}/cases/${caseId}/care-plan`,
    body,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

/**
 * The bytes and the name to save them under — not a link. The server publishes no URL for the
 * object, so the file only ever travels through an authorized request, and the name it chose comes
 * back in Content-Disposition rather than being invented here.
 */
export async function fetchCarePlan(
  caseId: string,
): Promise<{ blob: Blob; fileName: string }> {
  const response = await apiClient.get<Blob>(`${BASE_PATH}/cases/${caseId}/care-plan`, {
    responseType: 'blob',
  });
  return {
    blob: response.data,
    fileName: fileNameFromDisposition(
      (response.headers as Record<string, string | undefined>)['content-disposition'],
      'care-plan.pdf',
    ),
  };
}

export async function deleteCarePlan(caseId: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/cases/${caseId}/care-plan`);
}

export type SessionReturnStatus = 'PENDING' | 'APPROVED' | 'DENIED';

export type SessionReturnRequest = {
  id: string;
  caseId: string;
  requestedByName: string;
  requestedAt: string;
  reason: string | null;
  status: SessionReturnStatus;
  decidedAt: string | null;
  decisionNote: string | null;
};

export async function fetchSessionReturnRequests(
  status?: SessionReturnStatus,
): Promise<SessionReturnRequest[]> {
  const response = await apiClient.get<SessionReturnRequest[]>(`${BASE_PATH}/return-requests`, {
    params: status ? { status } : undefined,
  });
  return response.data;
}

/**
 * Approving sets the case to NOT_COMPLETED, and that is what returns the session — there is no
 * ledger entry to write.
 */
export async function decideSessionReturnRequest(
  requestId: string,
  approve: boolean,
  decisionNote?: string,
): Promise<SessionReturnRequest> {
  const response = await apiClient.post<SessionReturnRequest>(
    `${BASE_PATH}/return-requests/${requestId}/decision`,
    { approve, ...(decisionNote ? { decisionNote } : {}) },
  );
  return response.data;
}
